import { useState } from 'react';

import clsx from 'clsx';
import Select, { type SingleValue } from 'react-select';

import { useWindowSize } from '@quick/components/utils';
import { cn } from '@quick/utils';

import type { dataType, IIconSelect } from './data';
import { SelectContext } from './deal';
import { IconOption, IconSingleValue } from './dumbs';

function IconSelect({
  data,
  longPlaceholder,
  smallPlaceholder,
  onChange,
  defaultValue,
  windowWide = 768,
  className = '',
  selectStyles,
}: IIconSelect) {
  const [selectedOption, setSelectedOption] = useState<
    (typeof data)[0] | undefined
  >(data.find(({ value }) => value === defaultValue));
  const wide = useWindowSize(windowWide);
  const responsivePlaceholder = wide ? longPlaceholder : smallPlaceholder;

  // handle onChange event of the dropdown
  const handleChange = (newValue: SingleValue<dataType>) => {
    // Undefined instead of null to comply with typings
    setSelectedOption(newValue || undefined);
    onChange(newValue || undefined);
  };

  return (
    <SelectContext value={wide}>
      <div className={clsx('inline-flex', className)}>
        <Select
          className="w-full"
          classNames={{
            menuList: () =>
              'rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700',
            control: ({ isFocused }) =>
              cn(
                'dark:border-gray-600 dark:bg-gray-700',
                isFocused && 'border-gray-400 shadow-none'
              ),
            dropdownIndicator: () => 'dark:text-white',
            option: ({ isSelected, isFocused }) =>
              cn(
                '!cursor-pointer hover:bg-gray-light-2 hover:rounded hover:text-gray-800 hover:dark:bg-gray-600',
                isSelected && 'bg-gray-light-2 text-gray-800 dark:bg-gray-500',
                isFocused && 'bg-gray-light-2 dark:bg-gray-500'
              ),
          }}
          classNamePrefix="rs"
          placeholder={responsivePlaceholder}
          value={selectedOption}
          options={data}
          onChange={handleChange}
          isSearchable={false}
          components={{
            SingleValue: IconSingleValue,
            Option: IconOption,
            IndicatorSeparator: null,
          }}
          styles={{
            menuPortal: (provided) => ({
              ...provided,
              zIndex: 50,
            }),
            ...selectStyles,
          }}
          getOptionLabel={(e) => e?.text || responsivePlaceholder}
        />
      </div>
    </SelectContext>
  );
}

IconSelect.displayName = 'IconSelect';

export { IconSelect };
export type { IIconSelect };
